// Política de acesso do GPH: admin/partner → Pro → legado vitalício → Free limitado.
// Nunca confia em texto do usuário; só em entitlements/roles reais fornecidos pelo host.
//
// Vitalício legado é preservado EXATAMENTE como comprado:
// calculatorLifetime (PUBG) e freefireCalculatorLifetime (FF) liberam apenas a
// calculadora correspondente — nunca viram Pro completo.

import { getCapability } from './gphCapabilityRegistry';
import { isFreeLimitReached } from './gphFreeUsagePolicy';

export const ACCESS_REASONS = Object.freeze({
  OK: 'ok',
  OK_LEGACY: 'ok_legacy',
  OK_PRO: 'ok_pro',
  OK_PRIVILEGED: 'ok_privileged',
  PRO_REQUIRED: 'pro_required',
  FREE_LIMIT_REACHED: 'free_limit_reached',
  ADMIN_DENIED: 'admin_denied',
  PARTNER_DENIED: 'partner_denied',
  UNSUPPORTED: 'unsupported',
  LOGIN_REQUIRED: 'login_required'
});

const hasLegacyEntitlement = (capability, entitlements) => {
  if (!capability?.legacyEntitlementKeys?.length || !entitlements) return false;
  return capability.legacyEntitlementKeys.some((key) => entitlements[key] === true);
};

export const evaluateAccess = (capabilityOrId, context = {}) => {
  const capability = typeof capabilityOrId === 'string'
    ? getCapability(capabilityOrId)
    : capabilityOrId;

  const {
    user = null,
    entitlements = null,
    usage = {},
    hasAdminAccess = false,
    isPartner = false,
    hasPrivilegedAccess = false
  } = context;

  if (!capability) {
    return { allowed: false, reason: ACCESS_REASONS.UNSUPPORTED };
  }

  if (!user) {
    return { allowed: false, reason: ACCESS_REASONS.LOGIN_REQUIRED };
  }

  // Guards de permissão real — texto do usuário nunca é permissão.
  if (capability.requiresPlan === 'admin') {
    return hasAdminAccess
      ? { allowed: true, reason: ACCESS_REASONS.OK_PRIVILEGED }
      : { allowed: false, reason: ACCESS_REASONS.ADMIN_DENIED };
  }

  if (capability.requiresPlan === 'partner') {
    return (isPartner || hasAdminAccess)
      ? { allowed: true, reason: ACCESS_REASONS.OK_PRIVILEGED }
      : { allowed: false, reason: ACCESS_REASONS.PARTNER_DENIED };
  }

  if (capability.requiresPlan === 'any') {
    return { allowed: true, reason: ACCESS_REASONS.OK };
  }

  const isPro = entitlements?.isPro === true;

  if (capability.requiresPlan === 'pro_or_legacy') {
    if (hasPrivilegedAccess) return { allowed: true, reason: ACCESS_REASONS.OK_PRIVILEGED };
    if (isPro) return { allowed: true, reason: ACCESS_REASONS.OK_PRO };
    if (hasLegacyEntitlement(capability, entitlements)) {
      return { allowed: true, reason: ACCESS_REASONS.OK_LEGACY };
    }
    return { allowed: false, reason: ACCESS_REASONS.PRO_REQUIRED };
  }

  if (capability.requiresPlan === 'free_limited') {
    if (hasPrivilegedAccess || isPro) {
      return { allowed: true, reason: hasPrivilegedAccess ? ACCESS_REASONS.OK_PRIVILEGED : ACCESS_REASONS.OK_PRO };
    }
    if (isFreeLimitReached(usage, capability)) {
      return { allowed: false, reason: ACCESS_REASONS.FREE_LIMIT_REACHED };
    }
    return { allowed: true, reason: ACCESS_REASONS.OK };
  }

  return { allowed: true, reason: ACCESS_REASONS.OK };
};

export default evaluateAccess;
